import React from 'react';

import type { ActiveDefectLayerId } from 'state/surveyDataManager/activeLayersSlice';
import type { DefectMeasurementSlug, DefectSlug } from 'domain/surveyDataManager/contracts';
import {
  isWizardMeasurementFullyActive,
  WizardParameterOption,
} from '../wizardLogic';

export interface WizardStepParameterProps {
  readonly parameterOptions: readonly WizardParameterOption[];
  readonly activeLayerIds: ReadonlySet<ActiveDefectLayerId>;
  readonly selectedDefectSlug: DefectSlug | null;
  readonly selectedMeasurementSlug: DefectMeasurementSlug | null;
  readonly onSelectParameter: (option: WizardParameterOption) => void;
}

function WizardStepParameter({
  parameterOptions,
  activeLayerIds,
  selectedDefectSlug,
  selectedMeasurementSlug,
  onSelectParameter,
}: WizardStepParameterProps): JSX.Element {
  return (
    <div className="sdm-add-layer-wizard__panel">
      <h4 className="sdm-add-layer-wizard__panel-title">Choose parameter</h4>
      <p className="sdm-add-layer-wizard__hint">
        Choose the data to display. You will choose how it is visualised in the final step.
      </p>
      <div className="sdm-add-layer-wizard__card-grid sdm-add-layer-wizard__card-grid--wide">
        {parameterOptions.length === 0 ? (
          <p className="sdm-add-layer-wizard__hint" role="status">
            No parameters are available for this parameter group.
          </p>
        ) : null}
        {parameterOptions.map((option) => {
          const isSelected =
            selectedDefectSlug === option.defect.slug &&
            selectedMeasurementSlug === option.measurement.slug;
          const isDisabled = isWizardMeasurementFullyActive(
            activeLayerIds,
            option.defect,
            option.measurement,
          );

          return (
            <button
              key={`${option.defect.slug}:${option.measurement.slug}`}
              type="button"
              className={`sdm-add-layer-wizard__card${
                isSelected ? ' sdm-add-layer-wizard__card--selected' : ''
              }${isDisabled ? ' sdm-add-layer-wizard__card--disabled' : ''}`}
              onClick={() => onSelectParameter(option)}
              disabled={isDisabled}
              aria-disabled={isDisabled || undefined}
              aria-pressed={isSelected}
            >
              <strong>
                {option.measurement.name}
                {isDisabled ? ' (already active)' : ''}
              </strong>
              <p className="sdm-add-layer-wizard__card-description">
                {option.measurement.description}
              </p>
              <span className="sdm-add-layer-wizard__card-meta">
                {option.defect.name} · {option.measurement.code}
              </span>
              <span className="sdm-add-layer-wizard__card-meta">
                {option.measurement.surveyYears.length} survey year
                {option.measurement.surveyYears.length === 1 ? '' : 's'}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default WizardStepParameter;
